import { CsAgentRun, CsAgentSideEffect } from '../../../common/models/content-studio.model';
import { PendingActionSummary } from '../../../common/hooks/use-assistant-api.hook';

/**
 * The Work page: what the agents are doing, what needs a person, what is done.
 *
 * One list over every profile, deliberately. A run is a run whether the
 * studio planner or the assistant started it; the vendor should not need to
 * know which module to open to find out whether something finished.
 */

export type WorkLane = 'waiting' | 'running' | 'done';

/**
 * Something on the page that needs a person. Two shapes feed it: a run whose
 * plan proposed steps the vendor has not applied, and an assistant proposal
 * still pending on a conversation. Both become one row.
 */
export interface WaitingItem {
    key: string;
    source: 'run' | 'assistant';
    title: string;
    sideEffect: CsAgentSideEffect;
    creditCost?: number;
    at: string;
    run?: CsAgentRun;
    action?: PendingActionSummary;
}

/** A run that reached a proposal the vendor has not acted on. */
export const awaitsPerson = (run: CsAgentRun): boolean =>
    run.status === 'succeeded' && !run.applied && (run.steps ?? []).some((s) => s.status === 'proposed');

export const isLive = (run: CsAgentRun): boolean => run.status === 'pending' || run.status === 'running';

/**
 * A turn in a conversation, rather than a job someone set going.
 *
 * Both are runs and both are durable, but only one of them is WORK in the
 * sense a notification means. An Ask AI turn or a planner reply lands in the
 * panel the vendor is looking at, seconds after they pressed send — telling
 * them it has started, in the corner, is describing something they can already
 * see, and telling them it finished is describing something they just read.
 *
 * A conversation is the tell: these runs have somewhere of their own for the
 * answer to appear. A categorisation or a business analysis has nowhere, which
 * is the entire reason the toasts exist.
 */
export const isConversationTurn = (run: CsAgentRun): boolean =>
    !!run.threadId || typeof run.context?.assistantThreadUuid === 'string';

/**
 * A run that stopped to ask whether to keep spending.
 *
 * Deliberately neither running nor done. Showing it as running would spin a
 * spinner beside work that has stopped and will never restart on its own;
 * filing it as done would bury a decision the vendor has to make, and lose the
 * half-finished work it is holding when the conversation is eventually
 * discarded.
 */
export const isPaused = (run: CsAgentRun): boolean => run.status === 'paused';

/** The strongest class among a run's proposed steps — it decides the wording. */
const RANK: Record<CsAgentSideEffect, number> = { read: 0, write: 1, spend: 2, publish: 3 };

export const strongestSideEffect = (run: CsAgentRun): CsAgentSideEffect =>
    (run.steps ?? [])
        .filter((s) => s.status === 'proposed')
        .map((s) => s.sideEffect)
        .reduce<CsAgentSideEffect>((best, s) => (RANK[s] > RANK[best] ? s : best), 'write');

export function groupWork(
    runs: CsAgentRun[],
    actions: PendingActionSummary[],
): { waiting: WaitingItem[]; running: CsAgentRun[]; done: CsAgentRun[]; paused: CsAgentRun[] } {
    const waiting: WaitingItem[] = [
        ...actions.map<WaitingItem>((a) => ({
            key: `assistant:${a.actionId}`,
            source: 'assistant',
            title: a.summary,
            sideEffect: a.sideEffect ?? 'write',
            creditCost: a.creditCost,
            at: a.proposedAt,
            action: a,
        })),
        ...runs.filter(awaitsPerson).map<WaitingItem>((run) => ({
            key: `run:${run.runId}`,
            source: 'run',
            title: run.message,
            sideEffect: strongestSideEffect(run),
            at: run.updatedAt || run.createdAt,
            run,
        })),
    ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

    const running = runs.filter(isLive);
    const paused = runs.filter(isPaused);
    const done = runs.filter((r) => !isLive(r) && !isPaused(r) && !awaitsPerson(r));

    return { waiting, running, done, paused };
}

/** A vendor never reads a profile slug; unknown ones fall back to a neutral label. */
export const profileLabelKey = (profile: string): string => {
    switch (profile) {
        case 'content-planner':
            return 'WORK.PROFILE.CONTENT_PLANNER';
        case 'business-analyst':
            return 'WORK.PROFILE.BUSINESS_ANALYST';
        case 'categoriser':
            return 'WORK.PROFILE.CATEGORISER';
        case 'assistant':
            return 'WORK.PROFILE.ASSISTANT';
        default:
            return 'WORK.PROFILE.OTHER';
    }
};

/** `search_products` → "search products". The tool name is the verb. */
export const stepVerb = (tool: string): string => tool.replace(/[_-]+/g, ' ');

/** Where a run is followed up. Only studio runs have a place to open. */
/**
 * Where to send someone who wants to see what a run did.
 *
 * A studio run has a project to open. A categorisation run has no project —
 * its output is the category tree — so it goes to the page that shows it.
 * Without this the run is visible but leads nowhere, which is most of the
 * reason the old in-page panel felt necessary.
 */
export const runRoute = (run: CsAgentRun): string | null => {
    if (run.profile === 'categoriser') return '/products/categories';
    return run.projectUuid ? `/marketing/content-studio/${encodeURIComponent(run.projectUuid)}` : null;
};

/**
 * What a proposed step would do, in the vendor's words.
 *
 * Only the tools a vendor can be asked to approve get a sentence here; the
 * rest fall back to the verb. The arguments are the recorded call, so what
 * is described is exactly what approval runs.
 */
export function describeStep(
    step: { tool: string; args?: Record<string, unknown> },
    t: (key: string, opts?: Record<string, unknown>) => string,
): string {
    const args = step.args ?? {};
    switch (step.tool) {
        case 'schedule_post': {
            const when = args.scheduledAt ? new Date(String(args.scheduledAt)) : null;
            const caption = String(args.caption ?? '').trim();
            return t('WORK.STEP.SCHEDULE_POST', {
                when: when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : '—',
                caption: caption.length > 140 ? `${caption.slice(0, 140)}…` : caption,
            });
        }
        default:
            return stepVerb(step.tool);
    }
}

/** The proposed steps of a run, in order. */
export const proposedSteps = (run: CsAgentRun) => (run.steps ?? []).filter((s) => s.status === 'proposed');
